import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Card, Flex, Input, Space, Table, TablePaginationConfig } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { PagedResultState } from '../../../common/CommonResult';
import { API } from '../../../api';
import { notifyError, showConfirm } from '../../../store/utils';
import queryString from 'query-string';
import { CustomerScanStatus } from '../../customer/CustomerScanStatus';
import CustomerApiMember from './CustomerApiMember';

interface Filter {
  query?: string;
}

export const CustomerList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [modal, setModal] = useState({ visible: false, customer: null });
  const [apiModal, setApiModal] = useState<{ visible: boolean; customer: any | null }>({
    visible: false,
    customer: null,
  });
  const [filter, setFilter] = useState<Filter>({});

  const [state, setState] = useState<PagedResultState<any>>({
    loading: true,
    result: {},
  });

  const fetchCustomers = useCallback(async () => {
    setState({ ...state, loading: true });
    try {
      const { result } = await API.get({ apiVersion: 'api' })(`/customer/list${location.search}`);
      setState({
        loading: false,
        result,
      });
    } catch (error: any) {
      notifyError(error.message);
    }
  }, [location, state]);

  useEffect(() => {
    if (location.search) {
      fetchCustomers();
    }
  }, [location.search]);

  useEffect(() => {
    if (!location.search) {
      navigate(`/settings/customer?page=1&size=10`);
    }
  }, [location]);

  const onSearch = useCallback(() => {
    const params = queryString.parse(location.search);
    const search = {
      ...params,
      query: filter.query,
    };
    navigate(`/settings/customer?${queryString.stringify(search)}`);
  }, [filter, location]);

  const handleTableChange = useCallback(
    (pagination: TablePaginationConfig) => {
      const params = queryString.parse(location.search);
      const search = {
        ...params,
        page: pagination.current || 1,
        size: pagination.pageSize || 10,
      };
      navigate(`/settings/customer?${queryString.stringify(search)}`);
    },
    [location]
  );

  const confrimCustomerDelete = useCallback(
    (customer: any) => {
      showConfirm({
        title: 'Устгахдаа итгэлтэй байна уу?',
        content: 'Уг үйлдэл нь буцаагдахгүй!. Харилцагч устахыг анхаарна уу',
        onOk() {
          deleteCustomer(customer.no);
        },
        okText: 'Тийм',
        cancelText: 'Үгүй',
      });
    },
    [location]
  );

  const deleteCustomer = useCallback(
    async (customerNo: number) => {
      try {
        await API.delete({ apiVersion: 'api' })(`/customer/${customerNo}`);
        fetchCustomers();
      } catch (error: any) {
        notifyError(error.message);
      }
    },
    [location]
  );

  const columns = [
    {
      title: 'Салбар нэр',
      key: 'name',
      render: (record: any) => {
        return (
          <Link to={`/settings/branch/update/${record?.branch?.no}`}>{record?.branch?.name}</Link>
        );
      },
    },
    {
      title: 'Код',
      dataIndex: 'cd',
      key: 'cd',
    },
    {
      title: 'Харилцагчийн нэр',
      key: 'name',
      render: (record: any) => {
        return <Link to={`/settings/customer/update/${record.no}`}>{record.name}</Link>;
      },
    },
    {
      title: 'Утас 1',
      dataIndex: 'phone1',
      key: 'phone1',
    },
    {
      title: 'Утас 2',
      dataIndex: 'phone2',
      key: 'phone2',
    },
    {
      title: 'Хаяг',
      dataIndex: 'addressDetail',
      key: 'addressDetail',
      width: 300,
    },
    {
      title: 'Үйлдэл',
      width: 150,
      render: (record: any) => {
        return (
          <Flex vertical={false} gap={12}>
            <Link to={`/settings/customer/update/${record.no}`}>
              <Button size="small">Засах</Button>
            </Link>
            <Button onClick={() => setApiModal({ visible: true, customer: record })} size="small">
              Токен үүсгэх
            </Button>
            <Button
              size="small"
              type="primary"
              danger
              onClick={() => confrimCustomerDelete(record)}
            >
              Устгах
            </Button>
            <Button
              size="small"
              type="primary"
              onClick={() => setModal({ visible: true, customer: record.no })}
            >
              Status
            </Button>
          </Flex>
        );
      },
    },
  ];

  return (
    <Card
      title={`Харилцагчид - ${state.result?.total || 0}`}
      extra={
        <Link to={`/settings/customer/create`}>
          <Button type="primary" icon={<PlusOutlined />}>
            Харилцагч нэмэх
          </Button>
        </Link>
      }
    >
      <Space style={{ marginBottom: 16 }}>
        <Input.Search
          value={filter.query}
          placeholder="хайх"
          onChange={(e) => setFilter({ ...filter, query: e.target.value })}
          onSearch={onSearch}
        />
      </Space>
      <Table
        size="small"
        loading={state.loading}
        dataSource={state.result?.data || []}
        rowKey={'no'}
        pagination={{
          current: state?.result?.page || 1,
          size: 'small',
          pageSize: state?.result?.size || 10,
          total: state?.result?.total,
        }}
        onChange={handleTableChange}
        columns={columns}
      />
      <CustomerScanStatus
        visible={modal.visible}
        onClose={() => setModal({ visible: false, customer: null })}
        customer={modal.customer}
      />
      <CustomerApiMember
        visible={apiModal.visible}
        customer={apiModal.customer}
        onClose={() => setApiModal({ visible: false, customer: null })}
      />
    </Card>
  );
};
